<template>
  <section>
    <h4>
      <template v-if='selectedCityName'>
        {{
          tp(
            'storages_count_city',
            {
              count: filteredVisibleStorages.length,
              city: tp(`city_genitive.${selectedCityName}`, { defaultValue: selectedCityName })
            }
          )
        }}
      </template>
      <template v-else>
        {{ tp('storages_count', { count: filteredVisibleStorages.length }) }}
      </template>
    </h4>

    <div v-if='isMobile' class='filter'>
      <slot />
      <LabelButton
        v-for='(option, index) in sizeFilterOptions'
        :key='index'
        :is-active='option.isChecked'
        :is-disabled='option.isDisabled'
        :text='option.label'
        @click='toggleSizeFilter(option)'
      />
    </div>

    <Scrollable
      v-if='!isMobileMapActive'
      ref='scrollableElement'
      is-active
      class='scrollable'
    >
      <TransitionGroup
        tag='div'
        :class='{ "is-nothing-found": !filteredVisibleStorages.length }'
        :duration='isMobile ? 0 : 500'
      >
        <NothingFound
          v-if='!filteredVisibleStorages.length'
          key='-1'
          css-class='nothing-found-wrapper'
          :title='tpNothingFound("title")'
          :note='tpNothingFound("note")'
          :button='tpNothingFound("button")'
          @button:click='emit("filters:reset")'
        />
        <template v-else>
          <Storage
            v-for='storage in firstTwelveStorages'
            :key='storage.id'
            class='storage'
            v-bind='storage'
            @storage:click='emit("storage:click", storage.id)'
          />
        </template>
      </TransitionGroup>

      <div v-if='remainingStorages.length'>
        <Storage
          v-for='storage in remainingStorages'
          :key='storage.id'
          v-bind='storage'
          @storage:click='emit("storage:click", storage.id)'
        />
      </div>
    </Scrollable>
  </section>
</template>

<script>
export default {
  name: 'StoragesIndex',
  inheritAttrs: true // to inherit styles of parent container
};
</script>

<script setup>
import {
  ref,
  computed
} from 'vue';

import LabelButton from '../../components/label_button';
import NothingFound from '../nothing_found';
import Scrollable from '../scrollable';
import Storage from './storage';

import { useMobileDetect } from '../../composables/mobile_detect';

import { prepared } from '@/helpers/i18n';

const ANIMATED_STORAGES_THRESHOLD = 6;

const props = defineProps({
  filteredVisibleStorages: { type: Array, required: true },
  sizeFilterOptions: { type: Array, required: true },
  sizeFilter: { type: Object, required: true },
  isMobileMapActive: { type: Boolean, required: true },
  selectedCityName: { type: String, required: false, default: undefined }
});
const emit = defineEmits(['storage:click', 'toggle:size-filter', 'filters:reset']);
const tp = prepared('frontend.storages_map-v4.storages_index.index');
const tpNothingFound = prepared('frontend.storages_map-v4.storages_index.nothing_found');

const { isMobile } = useMobileDetect();

const scrollableElement = ref(null);
const prevScrollPosition = ref(null);

const animatedStoragesCount = computed(() => {
  if (!props.filteredVisibleStorages.length) return 0;

  return props.filteredVisibleStorages.length > ANIMATED_STORAGES_THRESHOLD * 2 ?
    ANIMATED_STORAGES_THRESHOLD :
    props.filteredVisibleStorages.length;
});

const firstTwelveStorages = computed(() =>
  props.filteredVisibleStorages.slice(0, animatedStoragesCount.value)
);

const remainingStorages = computed(() =>
  props.filteredVisibleStorages.slice(animatedStoragesCount.value)
);

function toggleSizeFilter(option) {
  if (option.isDisabled) return;
  emit('toggle:size-filter', option);
}

function getScrollPosition() {
  if (scrollableElement.value) {
    return scrollableElement.value.getScrollTop();
  }
  return 0;
};

function saveScrollPosition() {
  prevScrollPosition.value = getScrollPosition();

  return prevScrollPosition.value;
};

function restoreScrollPosition() {
  if (prevScrollPosition.value !== null && scrollableElement.value) {
    scrollableElement.value.scrollTo({ top: prevScrollPosition.value });
  }
};

function resetScroll() {
  if (scrollableElement.value) {
    scrollableElement.value.resetScroll();
  }
  prevScrollPosition.value = 0;
};

defineExpose({
  saveScrollPosition,
  restoreScrollPosition,
  resetScroll
});
</script>

<style scoped lang='sass'>
@import 'app/packs/stylesheets/functions/rem'
@import 'app/packs/stylesheets/mixins/responsive'
@import 'app/packs/stylesheets/mixins/typography'
@import 'app/packs/stylesheets/mixins/ltr_rtl'

h4
  margin: 0
  text-align: left

h4,
.filter
  +lte_ipad
    padding-left: var(--section-gap)
    padding-right: var(--section-gap)

.filter
  display: flex

  +lte_ipad
    gap: rem4(8px)
    margin-bottom: rem4(-12px)
    order: -1
    overflow-x: scroll
    padding-bottom: rem4(12px)

  +gte_laptop
    gap: 8px

.scrollable
  +lte_ipad
    overflow-x: hidden

// animations
.nothing-found-wrapper
  height: 100%
  top: 0
  left: 0
  position: absolute
  width: 100%

  &.v-enter-active,
  &.v-leave-active
    transition: all 500ms

  &.v-enter-from,
  &.v-leave-to
    opacity: 0

.storage
  &.v-move, /* apply transition to moving elements */
  &.v-enter-active,
  &.v-leave-active
    transition: all 500ms
    width: 100%

  /* ensure leaving items are taken out of layout flow so that moving
    animations can be calculated correctly. */
  &.v-leave-active
    position: absolute

  &.v-enter-from,
  &.v-leave-to
    opacity: 0

    +ltr
      transform: translateX(#{rem(45px)})

    +rtl
      transform: translateX(#{rem(-45px)})
</style>
